import React, { useState } from 'react';
import { Button, Tabs } from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { FilterWrapper } from 'src/components/common/wrapper/filter/wrapper';
import {
  Actions,
  AppArea,
  LmsCurriculumListFilterInput,
  SortingDirection,
  Subjects,
} from 'src/graphql/generated/graphql';
import { useLmsCurriculums } from 'src/hooks/query/lms/curriculums/use-curriculums';
import { useTable } from 'src/hooks/use-table';
import { LmsCurriculumsTable } from './table/table';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { Filter } from './filter';
import { useAuthContext } from 'src/context/auth.context';
import { useCan } from 'src/utils/can';

const PAGINATION_LIMIT = 10;

enum TabType {
  Active = 'active',
  Archived = 'archived',
}

export const LmsCurriculumList = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(TabType.Active);

  const { can } = useCan();
  const { isAdmin } = useAuthContext();
  const canCreate = can({
    action: Actions.Write,
    appArea: AppArea.Lms,
    subject: Subjects.Curriculums,
  });

  const tabs = [
    { label: t('common.active'), value: TabType.Active },
    { label: t('common.archived'), value: TabType.Archived },
  ];

  const {
    page,
    setPage,
    filter,
    onFilterChange,
    sorting,
    onSortChange,
    defaultFilter,
  } = useTable<
    LmsCurriculumListFilterInput,
    Record<string, SortingDirection>
  >();

  const { loading, data, totalCount } = useLmsCurriculums(
    {
      filter: {
        ...filter,
        isArchived: activeTab === TabType.Archived,
      },
      sorting,
      pagination: {
        page,
        limit: PAGINATION_LIMIT,
      },
    },
    { isAdmin },
  );

  return (
    <>
      <FilterWrapper>
        <Filter onSubmit={onFilterChange} defaultFilter={defaultFilter} />
      </FilterWrapper>

      <ContentWrapper>
        <TableWrapper>
          {canCreate && (
            <Tabs
              className="mb-2"
              tabs={tabs}
              onChange={(_, value) => setActiveTab(value)}
              value={activeTab}
              extra={
                <div className="flex justify-end">
                  <Link to={Routes.lmsCurriculumCreate.path}>
                    <Button variant="contained">
                      {t('lms.curriculum.list.create-button')}
                    </Button>
                  </Link>
                </div>
              }
            />
          )}

          <LmsCurriculumsTable
            data={data}
            className="mt-4"
            loading={loading}
            options={{
              page,
              count: totalCount,
              sort: true,
              onColumnSortChange: onSortChange,
              rowsPerPage: PAGINATION_LIMIT,
              onChangePage: setPage,
              enableNestedDataAccess: '.',
            }}
          />
        </TableWrapper>
      </ContentWrapper>
    </>
  );
};
